import { useState, useCallback, useMemo } from 'react';
import { Printer, Plus, Trash2, ArrowLeft, Eye, Type, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const TOPSTICK_8731 = {
  width: 48.3,
  height: 25.4,
  cols: 4,
  rows: 10,
  marginLeft: 8.1,
  marginTop: 21.2,
  perPage: 40,
};

const FONT_OPTIONS = [
  { id: 'montserrat', name: 'Montserrat', family: "'Montserrat', 'Roboto', sans-serif" },
  { id: 'oswald', name: 'Oswald', family: "'Oswald', 'Roboto', sans-serif" },
];

// Calculate optimal font size for text to fit in label
function calcFontSize(text, maxWidthMm, maxHeightMm) {
  if (!text) return 14;
  const maxWidthPt = maxWidthMm * 2.83;
  const maxHeightPt = maxHeightMm * 2.83;
  const charWidth = 0.6;
  const sizeByWidth = maxWidthPt / (text.length * charWidth);
  const sizeByHeight = maxHeightPt * 0.75;
  return Math.max(8, Math.min(28, Math.floor(Math.min(sizeByWidth, sizeByHeight))));
}

// Single name label preview
function NameLabel({ text, labelWidth, labelHeight, fontFamily }) {
  const displayText = (text || '').toUpperCase();
  const fontSize = calcFontSize(displayText, labelWidth - 4, labelHeight - 3);

  return (
    <div
      style={{
        width: `${labelWidth}mm`,
        height: `${labelHeight}mm`,
        padding: '1mm 1.5mm',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '0.3mm dashed #ccc',
        backgroundColor: 'white',
        overflow: 'hidden',
      }}
    >
      <span
        style={{
          fontSize: `${fontSize}pt`,
          fontWeight: 'bold',
          fontFamily,
          textAlign: 'center',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          lineHeight: 1.15,
          wordBreak: 'break-word',
          maxWidth: '100%',
        }}
      >
        {displayText || '—'}
      </span>
    </div>
  );
}

export default function NombreLabels() {
  const [names, setNames] = useState(['']);
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [fontId, setFontId] = useState('montserrat');
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [showConfig, setShowConfig] = useState(false);

  const font = FONT_OPTIONS.find((f) => f.id === fontId) || FONT_OPTIONS[0];
  const tpl = TOPSTICK_8731;

  const validNames = useMemo(() => names.filter((n) => n.trim()), [names]);
  const totalPages = Math.max(1, Math.ceil(validNames.length / tpl.perPage));

  const addName = () => setNames((prev) => [...prev, '']);
  const removeName = (idx) => setNames((prev) => prev.filter((_, i) => i !== idx));
  const updateName = (idx, val) =>
    setNames((prev) => prev.map((n, i) => (i === idx ? val : n)));

  const addMultiple = useCallback(() => {
    setNames((prev) => [...prev, '', '', '', '', '']);
  }, []);

  const handlePrint = () => {
    if (validNames.length === 0) return;
    const ox = offsetX || 0;
    const oy = offsetY || 0;
    const { width: lw, height: lh } = tpl;

    const pages = Math.ceil(validNames.length / tpl.perPage);
    const pagesHTML = Array.from({ length: pages }, (_, pageIdx) => {
      const start = pageIdx * tpl.perPage;
      const pageNames = validNames.slice(start, start + tpl.perPage);
      return `<div class="a4-page">${pageNames
        .map((name, i) => {
          const col = i % tpl.cols;
          const row = Math.floor(i / tpl.cols);
          const left = tpl.marginLeft + col * lw + ox;
          const top = tpl.marginTop + row * lh + oy;
          const display = name.toUpperCase();
          const fs = calcFontSize(display, lw - 4, lh - 3);
          return `<div class="label" style="position:absolute;left:${left}mm;top:${top}mm;">
            <span class="name" style="font-size:${fs}pt;">${display}</span>
          </div>`;
        })
        .join('')}</div>`;
    }).join('');

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`<!DOCTYPE html><html><head>
      <title>Etiquetas de Nombres</title>
      <link rel="preconnect" href="https://fonts.googleapis.com">
      <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
      <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@700&family=Oswald:wght@700&family=Roboto:wght@700&display=swap" rel="stylesheet">
      <style>
        @page { size: A4; margin: 0; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html, body { width: 210mm; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        body { font-family: ${font.family}; }
        .a4-page { width: 210mm; height: 297mm; position: relative; page-break-after: always; }
        .a4-page:last-child { page-break-after: auto; }
        .label {
          width: ${lw}mm; height: ${lh}mm;
          display: flex; align-items: center; justify-content: center;
          padding: 1mm 1.5mm; overflow: hidden;
        }
        .name {
          font-weight: 700; text-align: center; text-transform: uppercase;
          letter-spacing: 0.5px; line-height: 1.15; word-break: break-word;
          max-width: 100%; font-family: ${font.family};
        }
      </style></head><body>
      ${pagesHTML}
      <script>setTimeout(() => { window.print(); }, 300);<\/script>
    </body></html>`);
    printWindow.document.close();
  };

  return (
    <div className="space-y-6" data-testid="nombre-labels-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Type className="w-6 h-6 text-orange-500" />
            Etiquetas de Nombres
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Crea etiquetas con texto libre · Topstick 8731 (48.3 x 25.4 mm)
          </p>
        </div>
        <div className="flex gap-2">
          {!showFullPreview && (
            <Button
              onClick={() => setShowFullPreview(true)}
              disabled={validNames.length === 0}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="nombres-preview-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimir {validNames.length} etiqueta{validNames.length !== 1 ? 's' : ''}
            </Button>
          )}
        </div>
      </div>

      {!showFullPreview ? (
        <>
          {/* Config */}
          <div className="bg-white rounded-lg border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Opciones de formato</Label>
              <button
                className="text-xs text-blue-600 hover:underline"
                onClick={() => setShowConfig(!showConfig)}
              >
                {showConfig ? 'Ocultar' : 'Mostrar opciones'}
              </button>
            </div>
            {showConfig && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div>
                  <Label className="text-xs text-slate-500">Tipografía</Label>
                  <Select value={fontId} onValueChange={setFontId}>
                    <SelectTrigger data-testid="font-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FONT_OPTIONS.map((f) => (
                        <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Offset X (mm)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={offsetX}
                    onChange={(e) => setOffsetX(parseFloat(e.target.value) || 0)}
                    className="h-9"
                    data-testid="nombres-offset-x"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Offset Y (mm)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={offsetY}
                    onChange={(e) => setOffsetY(parseFloat(e.target.value) || 0)}
                    className="h-9"
                    data-testid="nombres-offset-y"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Names editor */}
          <div className="bg-white rounded-lg border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">
                Nombres ({names.length} campo{names.length !== 1 ? 's' : ''} · {validNames.length} con texto)
              </Label>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={addMultiple} data-testid="add-5-names-btn">
                  <Plus className="w-4 h-4 mr-1" /> +5
                </Button>
                <Button variant="outline" size="sm" onClick={addName} data-testid="add-name-btn">
                  <Plus className="w-4 h-4 mr-1" /> Añadir
                </Button>
              </div>
            </div>

            <ScrollArea className="max-h-[45vh]">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                {names.map((name, idx) => (
                  <div key={idx} className="flex gap-1 items-center">
                    <div className="relative flex-1">
                      <Input
                        value={name}
                        onChange={(e) => updateName(idx, e.target.value)}
                        placeholder={`Nombre ${idx + 1}`}
                        className="h-9 pr-8 font-semibold uppercase"
                        data-testid={`name-input-${idx}`}
                      />
                      {name && (
                        <button
                          onClick={() => updateName(idx, '')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    {names.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-9 w-9 p-0 text-slate-400 hover:text-red-500"
                        onClick={() => removeName(idx)}
                        data-testid={`remove-name-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>

            {/* Inline preview */}
            {validNames.length > 0 && (
              <div className="border-t pt-3 mt-3">
                <Label className="text-xs text-slate-400 mb-2 block">
                  Vista rápida (primeras {Math.min(validNames.length, 8)} etiquetas)
                </Label>
                <div className="flex flex-wrap gap-2 justify-center">
                  {validNames.slice(0, 8).map((n, i) => (
                    <NameLabel
                      key={i}
                      text={n}
                      labelWidth={tpl.width}
                      labelHeight={tpl.height}
                      fontFamily={font.family}
                    />
                  ))}
                  {validNames.length > 8 && (
                    <div className="flex items-center text-sm text-slate-400 px-4">
                      +{validNames.length - 8} más
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        /* Full sheet preview */
        <div className="space-y-4" data-testid="nombres-full-preview">
          <div className="text-sm text-slate-600 text-center">
            Verifica que los nombres estén correctamente posicionados antes de imprimir.
            <div className="text-xs text-amber-600 mt-1 font-medium">
              Importante: Configura "Escala: 100%" en el diálogo de impresión del navegador.
            </div>
          </div>

          <div className="flex flex-col items-center gap-6 pb-4 overflow-auto max-h-[60vh]">
            {Array.from({ length: totalPages }, (_, pageIdx) => {
              const start = pageIdx * tpl.perPage;
              const pageNames = validNames.slice(start, start + tpl.perPage);
              return (
                <div key={pageIdx} className="flex flex-col items-center">
                  <div className="text-xs text-slate-400 mb-1">
                    Página {pageIdx + 1} de {totalPages}
                  </div>
                  <div
                    style={{
                      width: `${210 * 0.45}mm`,
                      height: `${297 * 0.45}mm`,
                      overflow: 'hidden',
                      border: '2px solid #cbd5e1',
                      borderRadius: '6px',
                      position: 'relative',
                      backgroundColor: '#f1f5f9',
                    }}
                  >
                    <div
                      style={{
                        width: '210mm',
                        height: '297mm',
                        position: 'relative',
                        backgroundColor: 'white',
                        transform: 'scale(0.45)',
                        transformOrigin: 'top left',
                      }}
                    >
                      {pageNames.map((name, i) => {
                        const col = i % tpl.cols;
                        const row = Math.floor(i / tpl.cols);
                        const left = tpl.marginLeft + col * tpl.width + (offsetX || 0);
                        const top = tpl.marginTop + row * tpl.height + (offsetY || 0);
                        return (
                          <div
                            key={i}
                            style={{ position: 'absolute', left: `${left}mm`, top: `${top}mm` }}
                          >
                            <NameLabel
                              text={name}
                              labelWidth={tpl.width}
                              labelHeight={tpl.height}
                              fontFamily={font.family}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex gap-3 pt-2 border-t">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowFullPreview(false)}
              data-testid="nombres-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <Button
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              onClick={handlePrint}
              data-testid="nombres-confirm-print-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Confirmar impresión
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
